import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';

import Link from 'components/Link';
import Icon from 'components/Icon';
import { ButtonV3 } from 'components/ComponentV2';
import IconButton from 'components/IconButton';
import { GPTActionMenu } from 'components/candidate/GPTActionMenu/GPTActionMenu.component';
import { useCandidate } from 'hooks/useCandidate';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './GPTBanner.module.scss';

export const GPTBanner = ({ className }: { className?: string }) => {
  const [t] = useTranslation('opportunity');
  const { candidate } = useAuth();
  const { updateCVBuilderBanner } = useCandidate();
  const { opportunity, isOpportunityLoading, isOpportunityAppliedOrUnappliable } = useOpportunity();
  const { id, title, touchpointable_type, slug } = opportunity;
  const application = useMemo(() => {
    return {
      id,
      title,
      touchpointable_type,
      slug,
    };
  }, [id, title, touchpointable_type, slug]);

  if (isOpportunityLoading || !candidate?.show_cv_builder_banner || isOpportunityAppliedOrUnappliable) return null;

  const handleClose = async () => {
    try {
      await updateCVBuilderBanner({ show_cv_builder_banner: false });
    } catch (error) {
      console.error('Error updating CV builder banner', error);
    }
  };

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.content}>
        <Icon iconName="icon_magic-wand" size="mediumplus" />
        <p className={styles.title}>{t('gpt-banner.title')}</p>
        <IconButton iconName="icon_close" size="medium" className={styles['close-btn']} onClick={handleClose} />
      </div>
      <div className={styles['btns-container']}>
        <Link href={PAGE_ROUTES.CANDIDATE_PROFILE} underlineOnHover={false}>
          <ButtonV3 className={styles['btn']} variant="contained" color="secondary" size="small" borderless>
            {t('gpt-banner.cta')}
          </ButtonV3>
        </Link>
        <GPTActionMenu showIcon={false} iconClassName={styles['action-icon']} application={application} />
        <IconButton iconName="icon_close" size="small" className={styles['close-btn']} onClick={handleClose} />
      </div>
    </div>
  );
};

GPTBanner.displayName = 'GPTBanner';
